import { useCallback, useEffect, useState } from 'react';
import { Modal } from '@telegram-apps/telegram-ui';
import { get } from '../../helpers/api';
import { BarChart, type ChartPoint } from '../BarChart/BarChart';
import { ActivationLogModal } from '../ActivationLogModal/ActivationLogModal';
import { RecycleRecordModal } from '../RecycleRecordModal/RecycleRecordModal';
import styles from './ActivationChartModal.module.css';

const PERIOD_INFO: Record<number, string> = {
  1: '今天',
  2: '昨天',
  3: '7天',
  4: '本月',
  5: '上月',
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  period: number;
};

export const ActivationChartModal = ({ open, onOpenChange, period }: Props) => {
  const [points, setPoints] = useState<ChartPoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [logsOpen, setLogsOpen] = useState(false);
  const [recycleOpen, setRecycleOpen] = useState(false);

  const isHourly = period === 1 || period === 2;

  const fetchChart = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await get<ChartPoint[]>(
        `/admin/tel/activation/chart?period=${period}`
      );
      setPoints(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    if (open) {
      fetchChart();
    }
  }, [open, fetchChart]);

  const totalActivate = points.reduce((sum, p) => sum + (Number(p.activateCount) || 0), 0);
  const totalRecycle = points.reduce((sum, p) => sum + (Number(p.recycleCount) || 0), 0);
  const peak = points.reduce(
    (best, p) => {
      const total = (Number(p.activateCount) || 0) + (Number(p.recycleCount) || 0);
      return total > best.total ? { total, date: p.date, activate: Number(p.activateCount) || 0 } : best;
    },
    { total: 0, date: '', activate: 0 }
  );
  const fmtPeak = (date: string) =>
    isHourly ? date.slice(11, 16) : date.slice(5);
  const dayCount = isHourly ? 1 : points.length;
  const avgDaily = dayCount > 0 ? totalActivate / dayCount : 0;
  const fmtAvg = avgDaily.toLocaleString('zh-CN', { maximumFractionDigits: 1 });

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      className="app-modal"
      header={
        <div className={styles.banner}>
          <div className={styles.bannerTitle}>{PERIOD_INFO[period]}激活趋势</div>
          <div className={styles.bannerSub}>
            {isHourly
              ? '按小时统计当天激活与回收情况'
              : '按天统计周期内激活与回收情况'}
            ，蓝色柱为激活，浅灰柱为回收。
          </div>
          <div className={styles.bannerBars} aria-hidden="true">
            <i /><i /><i />
          </div>
        </div>
      }
    >
      <div className={styles.body}>
        {loading ? (
          <div className={styles.loading}>
            <span className="spinner" />
          </div>
        ) : error ? (
          <div className="error">{error}</div>
        ) : points.length > 0 ? (
          <>
            <div className={styles.summary}>
              <button
                type="button"
                className={`${styles.stat} ${styles.statClickable}`}
                onClick={() => setLogsOpen(true)}
              >
                <div className="stat-icon blue">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M13 2 4 14h7l-1 8 9-12h-7z" />
                  </svg>
                </div>
                <div className={styles.statLabel}>累计激活</div>
                <div className={styles.statValue}>
                  {totalActivate.toLocaleString()}
                </div>
              </button>
              <button
                type="button"
                className={`${styles.stat} ${styles.statClickable}`}
                onClick={() => setRecycleOpen(true)}
              >
                <div className="stat-icon green">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 12a9 9 0 0 1 15.5-6.2L21 8" />
                    <path d="M21 3v5h-5" />
                    <path d="M21 12a9 9 0 0 1-15.5 6.2L3 16" />
                    <path d="M3 21v-5h5" />
                  </svg>
                </div>
                <div className={styles.statLabel}>累计回收</div>
                <div className={styles.statValue}>
                  {totalRecycle.toLocaleString()}
                </div>
              </button>
              <div className={styles.stat}>
                <div className="stat-icon purple">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 19V9" />
                    <path d="M10 19V5" />
                    <path d="M16 19v-8" />
                    <path d="M22 19V3" />
                  </svg>
                </div>
                <div className={styles.statLabel}>最高峰值</div>
                <div className={styles.statValue}>
                  {peak.activate.toLocaleString()}
                  <span className={styles.statHint}>{fmtPeak(peak.date)}</span>
                </div>
              </div>
              <div className={styles.stat}>
                <div className="stat-icon orange">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 3v18h18" />
                    <path d="m7 14 3-3 3 3 4-5" />
                  </svg>
                </div>
                <div className={styles.statLabel}>日均激活</div>
                <div className={styles.statValue}>{fmtAvg}</div>
              </div>
            </div>
            <BarChart points={points} isHourly={isHourly} />
          </>
        ) : (
          <div className="error">暂无数据</div>
        )}
      </div>
      <ActivationLogModal
        nested
        open={logsOpen}
        onOpenChange={setLogsOpen}
        period={period}
      />
      <RecycleRecordModal
        nested
        open={recycleOpen}
        onOpenChange={setRecycleOpen}
        period={period}
      />
    </Modal>
  );
};